import {
	Box,
	Flex,
	HStack,
	IconButton,
	Image,
	Menu,
	MenuButton,
	MenuDivider,
	MenuItem,
	MenuList,
	Spacer,
	Stack,
	Text,
	useDisclosure,
	Drawer,
	DrawerBody,
	DrawerCloseButton,
	DrawerContent,
	DrawerHeader,
	DrawerOverlay,
	Avatar,
	Center,
} from "@chakra-ui/react";
import {
	BiMenu,
	BiHomeAlt,
	BiBarChartAlt2,
	BiEdit,
	BiMapAlt,
	BiUser,
	BiLogOut,
	BiChevronDown,
	BiPhone,
} from "react-icons/bi";
import { AiFillFacebook, AiOutlineInstagram } from "react-icons/ai";
import { Link, useLocation, useNavigate } from "react-router-dom";

import { useLangContext } from "configs/languageContext";
import { useAuthContext } from "configs/authContext";
import { LanguageSelector } from "components/shared/languageSelector";
import { translation } from "data/translations";

const mcgill_logo = require("images/mcgill.png");
const salve_monos_logo = require("images/salve_monos.png");

const C = {
	primary: "#059669",
	primaryDark: "#047857",
	primaryLight: "#ECFDF5",
	ink: "#0F172A",
	muted: "#475569",
	border: "#E2E8F0",
	bg: "#F8FAFC",
};

// ───────────────────────────────────────────────────────────────────────
// Public-site header (homepage, form, login). Clean, modern, no big monkey banner.
// ───────────────────────────────────────────────────────────────────────
export const SalveMonosHeader = () => (
	<Box w="100%" bg="white" borderBottomWidth="1px" borderColor={C.border}>
		<Flex
			maxW="1200px"
			mx="auto"
			align="center"
			px={{ base: 4, md: 6 }}
			py={3}
			gap={4}
		>
			<HStack as={Link} to="/" spacing={4}>
				<Image src={salve_monos_logo} alt="SalveMonos" h={["28px", "36px"]} />
				<Image src={mcgill_logo} alt="McGill University" h={["24px", "30px"]} opacity={0.9} />
			</HStack>

			<Spacer />

			<HStack spacing={1} display={{ base: "none", md: "flex" }}>
				<IconButton
					as="a"
					href="https://www.facebook.com/SalveMonosCR"
					target="_blank"
					rel="noreferrer"
					aria-label="Facebook"
					icon={<AiFillFacebook />}
					variant="ghost"
					size="sm"
					color={C.muted}
					_hover={{ color: C.primary }}
				/>
				<IconButton
					as="a"
					href="https://www.instagram.com/SalveMonosCR"
					target="_blank"
					rel="noreferrer"
					aria-label="Instagram"
					icon={<AiOutlineInstagram />}
					variant="ghost"
					size="sm"
					color={C.muted}
					_hover={{ color: C.primary }}
				/>
			</HStack>

			<LanguageSelector />
		</Flex>
	</Box>
);

// Used by public form
export const FormHeader = () => (
	<Flex w="100%" maxW="800px" mx="auto" mb={4} align="center" gap={3} px={2} py={2}>
		<HStack spacing={4}>
			<Image src={salve_monos_logo} alt="SalveMonos" h="36px" />
			<Image src={mcgill_logo} alt="McGill University" h="30px" opacity={0.9} />
		</HStack>
		<Spacer />
		<LanguageSelector />
	</Flex>
);

// Kept exported for backwards compat — now just an alias for the cleaner header.
export const MainHeader = () => null;
export const PageHeader = () => <SalveMonosHeader />;

// ───────────────────────────────────────────────────────────────────────
// Employee top nav — replaces the old left sidebar.
// ───────────────────────────────────────────────────────────────────────

interface NavItem {
	to: string;
	icon: any;
	labelKey: string; // translation key in `translation`
}

const NAV: NavItem[] = [
	{ to: "/employee/dashboard", icon: BiBarChartAlt2, labelKey: "emp_selector_vis" },
	{ to: "/employee/data",      icon: BiEdit,         labelKey: "emp_selector_data" },
	{ to: "/employee/map",       icon: BiMapAlt,       labelKey: "emp_selector_map" },
];

const NavLinkPill = ({
	to, icon: IconC, label, active,
}: { to: string; icon: any; label: string; active: boolean }) => (
	<Box
		as={Link}
		to={to}
		display="flex"
		alignItems="center"
		gap={2}
		px={3.5}
		py={2}
		borderRadius="lg"
		bg={active ? C.primaryLight : "transparent"}
		color={active ? C.primaryDark : C.ink}
		fontWeight={active ? "700" : "500"}
		fontSize="sm"
		_hover={{ bg: active ? C.primaryLight : C.bg, color: C.primaryDark }}
		transition="all .15s ease"
	>
		<IconC size="16" />
		{label}
	</Box>
);

export const EmployeeTopNav = () => {
	const { lang } = useLangContext();
	const { currentUser, logout } = useAuthContext();
	const navigate = useNavigate();
	const location = useLocation();
	const { isOpen, onOpen, onClose } = useDisclosure();

	const isActive = (to: string) => location.pathname.startsWith(to);

	const userInitial = (currentUser?.displayName || currentUser?.email || "?")
		.slice(0, 1)
		.toUpperCase();

	const accountLabel = lang === "es" ? "Mi cuenta" : "My account";
	const logoutLabel = lang === "es" ? "Cerrar sesión" : "Sign out";

	return (
		<>
			<Box
				position="sticky"
				top={0}
				zIndex={20}
				bg="rgba(255,255,255,0.85)"
				borderBottomWidth="1px"
				borderColor={C.border}
				backdropFilter="blur(10px)"
			>
				<Flex maxW="1280px" mx="auto" align="center" px={{ base: 4, md: 6 }} py={3} gap={3}>
					{/* Logos only */}
					<HStack as={Link} to="/" spacing={3}>
						<Image src={salve_monos_logo} alt="SalveMonos" h="32px" />
						<Image src={mcgill_logo} alt="McGill University" h="26px" opacity={0.9} display={{ base: "none", sm: "block" }} />
					</HStack>

					<Spacer />

					{/* Desktop nav */}
					<HStack spacing={1} display={{ base: "none", md: "flex" }}>
						{NAV.map((n) => (
							<NavLinkPill
								key={n.to}
								to={n.to}
								icon={n.icon}
								label={translation[n.labelKey]?.[lang] ?? n.labelKey}
								active={isActive(n.to)}
							/>
						))}
					</HStack>

					<Spacer display={{ base: "none", md: "block" }} />

					<HStack spacing={2}>
						<LanguageSelector staff />

						{/* Account dropdown */}
						<Menu>
							<MenuButton
								as={Box}
								cursor="pointer"
								px={2}
								py={1}
								borderRadius="full"
								_hover={{ bg: C.bg }}
								display="flex"
								alignItems="center"
							>
								<HStack spacing={2}>
									<Avatar size="sm" name={userInitial} bg={C.primary} color="white" fontWeight="700" />
									<Box display={{ base: "none", md: "block" }} textAlign="left" lineHeight="1">
										<Text fontSize="xs" color={C.muted} mt={0.5}>{lang === "es" ? "Sesión" : "Signed in"}</Text>
										<Text fontSize="xs" color={C.ink} fontWeight="600" maxW="160px" noOfLines={1}>
											{currentUser?.email || "—"}
										</Text>
									</Box>
									<Box display={{ base: "none", md: "block" }} color={C.muted}>
										<BiChevronDown />
									</Box>
								</HStack>
							</MenuButton>
							<MenuList borderColor={C.border} borderRadius="xl" boxShadow="lg" minW="220px">
								<Box px={3} py={2}>
									<Text fontSize="xs" color={C.muted}>{lang === "es" ? "Sesión iniciada como" : "Signed in as"}</Text>
									<Text fontSize="sm" fontWeight="700" color={C.ink} noOfLines={1}>{currentUser?.email}</Text>
								</Box>
								<MenuDivider borderColor={C.border} />
								<MenuItem icon={<BiUser />} onClick={() => navigate("/employee/account")}>
									{accountLabel}
								</MenuItem>
								<MenuItem icon={<BiHomeAlt />} onClick={() => navigate("/")}>
									{lang === "es" ? "Sitio público" : "Public site"}
								</MenuItem>
								<MenuDivider borderColor={C.border} />
								<MenuItem icon={<BiLogOut />} onClick={logout} color="red.600">
									{logoutLabel}
								</MenuItem>
							</MenuList>
						</Menu>

						{/* Mobile burger */}
						<IconButton
							aria-label="menu"
							icon={<BiMenu />}
							variant="ghost"
							size="md"
							display={{ base: "flex", md: "none" }}
							onClick={onOpen}
						/>
					</HStack>
				</Flex>
			</Box>

			{/* Mobile drawer */}
			<Drawer isOpen={isOpen} placement="left" onClose={onClose} size="xs">
				<DrawerOverlay />
				<DrawerContent>
					<DrawerCloseButton />
					<DrawerHeader borderBottomWidth="1px" borderColor={C.border}>
						<HStack spacing={3}>
							<Image src={salve_monos_logo} alt="SalveMonos" h="28px" />
							<Image src={mcgill_logo} alt="McGill University" h="22px" opacity={0.9} />
						</HStack>
					</DrawerHeader>
					<DrawerBody p={3}>
						<Stack spacing={1}>
							{NAV.map((n) => (
								<Box key={n.to} onClick={onClose}>
									<NavLinkPill
										to={n.to}
										icon={n.icon}
										label={translation[n.labelKey]?.[lang] ?? n.labelKey}
										active={isActive(n.to)}
									/>
								</Box>
							))}
							<Box pt={3} mt={3} borderTopWidth="1px" borderColor={C.border}>
								<NavLinkPill to="/employee/account" icon={BiUser} label={accountLabel} active={isActive("/employee/account")} />
								<Box mt={1} onClick={() => { logout(); onClose(); }}>
									<NavLinkPill to="#" icon={BiLogOut} label={logoutLabel} active={false} />
								</Box>
							</Box>
							<Box pt={3} mt={3} borderTopWidth="1px" borderColor={C.border} display="flex" alignItems="center" justifyContent="space-between">
								<Text fontSize="xs" color={C.muted}>{lang === "es" ? "Idioma" : "Language"}</Text>
								<LanguageSelector staff />
							</Box>
						</Stack>
					</DrawerBody>
				</DrawerContent>
			</Drawer>
		</>
	);
};

// Quick contact strip (optional, used at the bottom of public pages elsewhere)
export const ContactStrip = ({ lang }: { lang: string }) => (
	<Center bg={C.bg} py={2} borderTopWidth="1px" borderColor={C.border}>
		<HStack spacing={2} fontSize="sm" color={C.muted}>
			<BiPhone />
			<Text>
				{lang === "es" ? "Emergencia: " : "Emergency: "}
				<Box as="a" href="tel:+50684029264" fontWeight="700" color={C.primary}>+506 8402 9264</Box>
			</Text>
		</HStack>
	</Center>
);
